import { Target, Eye, Heart, Leaf, Globe, Users, Award, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_590a4cbe-6d2f-42fa-9080-72b33b8524af/artifacts/4pnrtt86_AP-Founder.PNG";

const whatWeDo = [
  {
    title: "Carbon Credit Marketplace",
    description: "A comprehensive platform for registering, buying, and selling verified carbon credits. We connect project developers with buyers seeking quality offsets.",
    icon: TrendingUp,
    color: "bg-emerald-50 text-emerald-700"
  },
  {
    title: "Climate Participation Programs",
    description: "Our Green Programs (GreenStay, GreenTrack, FlyGreen, etc.) enable individuals and institutions to offset their carbon footprint easily.",
    icon: Leaf,
    color: "bg-teal-50 text-teal-700"
  },
  {
    title: "CBAM & Compliance Advisory",
    description: "Expert guidance for Indian exporters navigating CBAM requirements, carbon footprint assessment, and sustainability reporting.",
    icon: Globe,
    color: "bg-blue-50 text-blue-700"
  },
  {
    title: "Climate Infrastructure Development",
    description: "End-to-end project development for renewable energy, waste management, and carbon capture infrastructure across India.",
    icon: Award,
    color: "bg-amber-50 text-amber-700"
  }
];

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="about-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-forest-50 via-white to-earth-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            About <span className="text-forest-700">The Carbon Meridian</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            India's platform for carbon markets, climate participation, and sustainability leadership. 
            We bridge the gap between climate action and everyday participation.
          </p>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Vision */}
            <Card className="border-2 border-forest-100 hover:border-forest-200 transition-colors">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-forest-100 rounded-2xl flex items-center justify-center">
                    <Eye className="h-7 w-7 text-forest-700" />
                  </div>
                  <h2 className="font-montserrat text-2xl font-bold text-slate-900">Our Vision</h2>
                </div>
                <p className="text-lg text-slate-600 leading-relaxed">
                  To be India's default ecosystem for carbon markets and climate action — a platform where 
                  every individual, institution, and enterprise can meaningfully participate in building 
                  a sustainable future. We envision a carbon-conscious India where climate responsibility 
                  is accessible to all.
                </p>
              </CardContent>
            </Card>

            {/* Mission */}
            <Card className="border-2 border-earth-100 hover:border-earth-200 transition-colors">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-earth-100 rounded-2xl flex items-center justify-center">
                    <Target className="h-7 w-7 text-earth-700" />
                  </div>
                  <h2 className="font-montserrat text-2xl font-bold text-slate-900">Our Mission</h2>
                </div>
                <p className="text-lg text-slate-600 leading-relaxed">
                  To democratize climate action by making carbon markets accessible, providing world-class 
                  climate education, enabling seamless carbon offsetting, and supporting businesses in their 
                  sustainability journey. We are committed to India's Net Zero 2070 target and 45% carbon 
                  intensity reduction by 2030.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-montserrat text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What We Do
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A comprehensive ecosystem for India's carbon economy
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {whatWeDo.map((item) => (
              <Card key={item.title} className="h-full hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-montserrat text-xl font-semibold text-slate-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-montserrat text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Our Values
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="h-8 w-8 text-emerald-700" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Sustainability</h3>
              <p className="text-sm text-slate-600">Climate action at the core of everything we do</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-blue-700" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Transparency</h3>
              <p className="text-sm text-slate-600">Clear, verified, and trustworthy climate solutions</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-purple-700" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Accessibility</h3>
              <p className="text-sm text-slate-600">Making climate action possible for everyone</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-amber-700" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Impact</h3>
              <p className="text-sm text-slate-600">Measurable contribution to India's climate goals</p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-forest-950 to-earth-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="flex justify-center lg:justify-start">
              <div className="relative">
                <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-2xl" />
                <div className="relative bg-white/10 p-4 rounded-3xl backdrop-blur-sm">
                  <img
                    src={FOUNDER_IMAGE}
                    alt="Arun Punia - Founder"
                    className="w-64 h-64 sm:w-80 sm:h-80 object-cover rounded-2xl"
                    data-testid="founder-image"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <p className="text-sm font-medium text-emerald-400 uppercase tracking-wider mb-2">
                Founder's Message
              </p>
              <h2 className="font-montserrat text-3xl sm:text-4xl font-bold mb-4">
                Arun Punia
              </h2>
              <div className="space-y-4 text-forest-100 leading-relaxed">
                <p>
                  "Climate change is the defining challenge of our generation. At The Carbon Meridian, 
                  we believe that every individual and organization has a role to play in addressing it."
                </p>
                <p>
                  "We founded this platform to democratize climate action in India — to make carbon markets 
                  accessible, climate education available, and sustainability achievable for all."
                </p>
                <p>
                  "Our vision extends beyond just offsetting emissions. We aim to create a comprehensive 
                  ecosystem where participation in climate action becomes as natural as any other aspect 
                  of life and business."
                </p>
                <p>
                  "Join us in this journey. Together, we can transform footprints into futures — one 
                  carbon credit, one course, one contribution at a time."
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-emerald-400 font-medium">From Footprints to Futures</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
